import React, { useState, useCallback } from 'react';
import { Edit2, Eye, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { cn } from '../../lib/utils';
import { HsmTemplate, HsmTemplatesResponse } from '../../types';
import { getTemplateStatusBadge, getTemplateBodyText } from './templateHelpers';
import {
  TemplatePreviewDialog,
  DeleteTemplateDialog,
  CreateTemplateDialog,
  EditTemplateDialog,
} from './dialogs';

export interface HsmTemplatesTabProps {
  // State
  templates: HsmTemplate[];
  templatesLoading: boolean;
  templatesResponse: HsmTemplatesResponse | null;
  templateCreating: boolean;
  templateUpdating: boolean;
  selectedTemplate: HsmTemplate | null;
  editTemplateDialogOpen: boolean;
  setEditTemplateDialogOpen: (open: boolean) => void;

  // Handlers
  fetchTemplates: () => Promise<{ success: boolean; error?: string }>;
  handleCreateTemplate: (data: {
    name: string;
    language: string;
    category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
    bodyText: string;
    headerText?: string;
    headerType?: 'TEXT';
    footerText?: string;
    bodyExamples?: string[];
    headerExample?: string;
  }) => Promise<{ success: boolean; error?: string }>;
  handleUpdateTemplate: (
    templateId: string,
    data: {
      bodyText: string;
      headerText?: string;
      headerType?: 'TEXT';
      footerText?: string;
      bodyExamples?: string[];
      headerExample?: string;
    }
  ) => Promise<{ success: boolean; error?: string }>;
  openEditTemplateDialog: (template: HsmTemplate) => void;
  closeEditTemplateDialog: () => void;
  saveTemplateVariableMapping: (
    templateName: string,
    variableMapping: string[]
  ) => Promise<{ success: boolean; error?: string }>;
  getTemplateVariableMapping: (
    templateName: string
  ) => Promise<{ success: boolean; variableMapping?: string[] | null; error?: string }>;
  handleDeleteTemplate: (templateName: string) => Promise<{ success: boolean; error?: string }>;
  templateDeleting: boolean;
}

export function HsmTemplatesTab({
  templates,
  templatesLoading,
  templatesResponse,
  templateCreating,
  templateUpdating,
  selectedTemplate,
  editTemplateDialogOpen,
  setEditTemplateDialogOpen,
  fetchTemplates,
  handleCreateTemplate,
  handleUpdateTemplate,
  openEditTemplateDialog,
  closeEditTemplateDialog,
  saveTemplateVariableMapping,
  getTemplateVariableMapping,
  handleDeleteTemplate,
  templateDeleting,
}: HsmTemplatesTabProps) {
  // Create template dialog state
  const [createTemplateDialogOpen, setCreateTemplateDialogOpen] = useState(false);

  // Template preview dialog state
  const [templatePreviewOpen, setTemplatePreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<HsmTemplate | null>(null);

  // Delete template dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<HsmTemplate | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccessMessage, setDeleteSuccessMessage] = useState<string | null>(null);

  // Handler to open template preview
  const handlePreviewTemplate = useCallback((template: HsmTemplate) => {
    setPreviewTemplate(template);
    setTemplatePreviewOpen(true);
  }, []);

  // Open delete confirmation dialog
  const openDeleteDialog = useCallback((template: HsmTemplate) => {
    setTemplateToDelete(template);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  }, []);

  // Handle delete template confirmation
  const handleDeleteConfirm = useCallback(async () => {
    if (!templateToDelete) return;

    setDeleteError(null);

    const result = await handleDeleteTemplate(templateToDelete.name);

    if (result.success) {
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      setDeleteSuccessMessage(`Template "${templateToDelete.name}" excluido com sucesso.`);
      // Clear success message after 5 seconds
      setTimeout(() => setDeleteSuccessMessage(null), 5000);
    } else {
      setDeleteError(result.error || 'Erro ao excluir template');
    }
  }, [templateToDelete, handleDeleteTemplate]);

  // Reset delete dialog state
  const resetDeleteDialog = useCallback(() => {
    setTemplateToDelete(null);
    setDeleteError(null);
    setDeleteDialogOpen(false);
  }, []);

  return (
    <>
      <div className="space-y-4">
        {/* Template Stats */}
        {templatesResponse && (
          <div className="grid gap-3 md:grid-cols-4 mb-4">
            <div className="rounded-lg border bg-card p-3">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {templatesResponse.countsByStatus.approved}
              </div>
              <div className="text-xs text-muted-foreground">Aprovados</div>
            </div>
            <div className="rounded-lg border bg-card p-3">
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {templatesResponse.countsByStatus.pending}
              </div>
              <div className="text-xs text-muted-foreground">Pendentes</div>
            </div>
            <div className="rounded-lg border bg-card p-3">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {templatesResponse.countsByStatus.rejected}
              </div>
              <div className="text-xs text-muted-foreground">Rejeitados</div>
            </div>
            <div className="rounded-lg border bg-card p-3">
              <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                {templatesResponse.countsByStatus.disabled}
              </div>
              <div className="text-xs text-muted-foreground">Desativados</div>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center">
          <p className="text-sm text-muted-foreground">
            Templates de mensagem disponiveis no Meta Business Suite.
          </p>
          <div className="flex gap-2">
            <Button onClick={() => setCreateTemplateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Criar Template
            </Button>
            <Button variant="outline" onClick={fetchTemplates} disabled={templatesLoading}>
              <RefreshCw className={cn('mr-2 h-4 w-4', templatesLoading && 'animate-spin')} />
              Atualizar
            </Button>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-3">
          {templatesLoading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin" />
            </div>
          ) : templates.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">
              Nenhum template encontrado. Clique em "Atualizar" para carregar.
            </p>
          ) : (
            templates.map((template) => (
              <Card key={template.id} className="shadow-sm">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium">{template.name}</div>
                      <div className="text-sm text-muted-foreground">{template.language}</div>
                    </div>
                    {getTemplateStatusBadge(template.status)}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <div className="text-xs text-muted-foreground">Categoria</div>
                      <div className="font-medium">{template.category}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">ID</div>
                      <div className="text-xs truncate">{template.id}</div>
                    </div>
                  </div>
                  {getTemplateBodyText(template) && (
                    <div className="p-2 rounded bg-muted/50 text-xs text-muted-foreground">
                      {getTemplateBodyText(template).substring(0, 100)}
                      {getTemplateBodyText(template).length > 100 && '...'}
                    </div>
                  )}
                  <div className="flex gap-2 pt-2 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => openEditTemplateDialog(template)}
                    >
                      <Edit2 className="mr-2 h-4 w-4" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handlePreviewTemplate(template)}
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      Ver Preview
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => openDeleteDialog(template)}
                      title="Excluir template"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>Idioma</TableHead>
                <TableHead className="max-w-md">Preview</TableHead>
                <TableHead className="text-right">Acoes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templatesLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : templates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm">
                    Nenhum template encontrado. Clique em "Atualizar" para carregar.
                  </TableCell>
                </TableRow>
              ) : (
                templates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell className="font-medium">{template.name}</TableCell>
                    <TableCell>{getTemplateStatusBadge(template.status)}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{template.category}</Badge>
                    </TableCell>
                    <TableCell>{template.language}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-md truncate">
                      {getTemplateBodyText(template).substring(0, 80)}
                      {getTemplateBodyText(template).length > 80 && '...'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditTemplateDialog(template)}
                          title="Editar template"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePreviewTemplate(template)}
                          title="Ver preview"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => openDeleteDialog(template)}
                          title="Excluir template"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Template Preview Dialog */}
      <TemplatePreviewDialog
        open={templatePreviewOpen}
        onOpenChange={setTemplatePreviewOpen}
        template={previewTemplate}
      />

      {/* Create Template Dialog */}
      <CreateTemplateDialog
        open={createTemplateDialogOpen}
        onOpenChange={setCreateTemplateDialogOpen}
        creating={templateCreating}
        onSubmit={handleCreateTemplate}
        onSaveVariableMapping={saveTemplateVariableMapping}
      />

      {/* Edit Template Dialog */}
      <EditTemplateDialog
        open={editTemplateDialogOpen}
        onOpenChange={setEditTemplateDialogOpen}
        template={selectedTemplate}
        updating={templateUpdating}
        onSubmit={handleUpdateTemplate}
        onClose={closeEditTemplateDialog}
        onSaveVariableMapping={saveTemplateVariableMapping}
        onGetVariableMapping={getTemplateVariableMapping}
      />

      {/* Delete Template Confirmation Dialog */}
      <DeleteTemplateDialog
        open={deleteDialogOpen}
        template={templateToDelete}
        deleting={templateDeleting}
        error={deleteError}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={resetDeleteDialog}
      />

      {/* Success message toast */}
      {deleteSuccessMessage && (
        <div className="fixed bottom-4 right-4 z-50 animate-in fade-in slide-in-from-bottom-4">
          <div className="rounded-lg border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800 px-4 py-3 shadow-lg">
            <p className="text-sm text-green-700 dark:text-green-300">{deleteSuccessMessage}</p>
          </div>
        </div>
      )}
    </>
  );
}
